import React, { useState, useRef } from 'react';
import {
  Box, Button, TextField, Typography, Paper, LinearProgress, Stack, FormControl, InputLabel, OutlinedInput, FormGroup, FormLabel, Divider
} from '@mui/material';

const API_BASE = "https://apigw.cv2-dev.harmonate.com/api";
const PART_SIZE = 5 * 1024 * 1024; // 5MB

function sanitizeId(name) {
  return name.replace(/[^a-zA-Z0-9]/g, "");
}

const Test = () => {
  const [tenantId, setTenantId] = useState('123e4567-e89b-12d3-a456-426614174000');
  const [unitOfWorkName, setUnitOfWorkName] = useState('Test UnitOfWork');
  const [files, setFiles] = useState([]);
  const [documentType, setDocumentType] = useState('');
  const [fundId, setFundId] = useState('');
  const [investorId, setInvestorId] = useState('');
  const [holdingId, setHoldingId] = useState('');
  const [log, setLog] = useState([]);
  const [progress, setProgress] = useState({}); // { fileName: percent }
  const [uploading, setUploading] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const fileInputRef = useRef();

  const appendLog = (msg, type = 'info') => {
    setLog(lg => [...lg, { msg, type }]);
  };

  const logJson = (obj, label = "") => {
    appendLog((label ? label + ':\n' : '') + JSON.stringify(obj, null, 2), 'json');
  };

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setLog([]);
    setProgress({});
    setUploading(true);
    if (!tenantId || !unitOfWorkName || !token || files.length === 0) {
      appendLog('Please fill in all fields and select files.', 'error');
      setUploading(false);
      return;
    }
    try {
      // Prepare file metadata
      const fileMetas = files.map(file => ({
        name: file.name,
        content_type: file.type,
        document_type_id: documentType || null,
        fund_id: fundId || null,
        investor_id: investorId || null,
        holding_id: holdingId || null
      }));
      // 1. Create Unit of Work
      const uowRes = await fetch(`${API_BASE}/${tenantId}/units-of-work`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: unitOfWorkName, documents: fileMetas })
      });
      const uowResponse = await uowRes.json();
      appendLog(`✅ Unit of Work created successfully. ID: ${uowResponse.uuid}`, 'success');
      // 2. For each file, upload in parts
      for (const file of files) {
        const fileData = uowResponse.documents.find(f => f.name === file.name);
        if (!fileData) continue;
        const totalParts = Math.ceil(file.size / PART_SIZE);
        // 2a. Get presigned URLs
        const urlsRes = await fetch(`${API_BASE}/multipart/urls`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            id: fileData.id,
            tenant_id: fileData.tenant_id,
            unit_of_work_id: fileData.unit_of_work_id,
            location: fileData.location,
            upload_id: fileData.upload_id,
            name: file.name,
            content_type: file.type,
            total_parts: totalParts
          })
        });
        const urlsJson = await urlsRes.json();
        const parts = [];
        for (let i = 0; i < totalParts; i++) {
          const start = i * PART_SIZE;
          const end = Math.min(start + PART_SIZE, file.size);
          const partBlob = file.slice(start, end);
          const partNumber = i + 1;
          const urlObj = urlsJson.urls.find(p => p.part_number === partNumber);
          // 2b. Upload part
          const uploadRes = await fetch(urlObj.url, {
            method: 'PUT',
            body: partBlob
          });
          const etag = uploadRes.headers.get('ETag')?.replaceAll('"', '');
          parts.push({ etag, part_number: partNumber });
          setProgress(prg => ({ ...prg, [file.name]: Math.round(((i + 1) / totalParts) * 100) }));
        }
        // 2c. Complete upload
        const completeRes = await fetch(`${API_BASE}/multipart/complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            id: fileData.id,
            unit_of_work_id: fileData.unit_of_work_id,
            location: fileData.location,
            upload_id: fileData.upload_id,
            name: file.name,
            content_type: file.type,
            parts
          })
        });
        const completeJson = await completeRes.json();
        logJson(completeJson, `Completed upload for ${file.name}`);
      }
      appendLog('✅ All uploads completed successfully.', 'success');
    } catch (err) {
      appendLog('Upload failed: ' + err.message, 'error');
    }
    setUploading(false);
  };

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', py: 4 }}>
      <Box sx={{ maxWidth: 800, mx: 'auto', background: '#fff', borderRadius: 2, boxShadow: 2, p: 4 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ color: '#00b050', fontWeight: 700 }}>
          Upload Files to S3 (Multipart)
        </Typography>
        <form onSubmit={handleUpload} id="upload-form">
          <TextField
            label="Tenant ID"
            value={tenantId}
            onChange={e => setTenantId(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Unit of Work Name"
            value={unitOfWorkName}
            onChange={e => setUnitOfWorkName(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ my: 2 }}
          >
            Select Files
            <input
              type="file"
              multiple
              hidden
              ref={fileInputRef}
              onChange={handleFileChange}
              required
            />
          </Button>
          <Box sx={{ mb: 2 }}>
            {files.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {files.length} file(s) selected: {files.map(f => f.name).join(', ')}
              </Typography>
            )}
          </Box>
          <TextField
            label="Authorization Token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            fullWidth
            margin="normal"
            required
            helperText="Token is auto-filled from localStorage but can be edited."
          />
          <Divider sx={{ my: 3 }}>Optional Fields</Divider>
          <TextField
            label="Document Type ID"
            value={documentType}
            onChange={e => setDocumentType(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="Optional: Document type ID"
          />
          <TextField
            label="Fund ID"
            value={fundId}
            onChange={e => setFundId(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="Optional: Fund ID"
          />
          <TextField
            label="Investor ID"
            value={investorId}
            onChange={e => setInvestorId(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="Optional: Investor ID"
          />
          <TextField
            label="Holding ID"
            value={holdingId}
            onChange={e => setHoldingId(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="Optional: Holding ID"
          />
          <Button
            type="submit"
            variant="contained"
            color="success"
            fullWidth
            sx={{ mt: 3, fontWeight: 700 }}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Start Upload'}
          </Button>
        </form>
        {/* Progress bars */}
        <Box sx={{ mt: 3 }}>
          {files.map(file => (
            <Box key={file.name} sx={{ mb: 2 }}>
              <Typography variant="body2">{file.name}</Typography>
              <LinearProgress variant="determinate" value={progress[file.name] || 0} sx={{ height: 10, borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {progress[file.name] ? `${progress[file.name]}%` : '0%'}
              </Typography>
            </Box>
          ))}
        </Box>
        {/* Log output */}
        <Paper variant="outlined" sx={{ mt: 4, p: 2, bgcolor: '#f9f9f9', minHeight: 100 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Log</Typography>
          <Box sx={{ fontFamily: 'monospace', fontSize: '0.95em', whiteSpace: 'pre-wrap' }}>
            {log.map((entry, idx) => (
              <Box key={idx} sx={{ color: entry.type === 'error' ? 'red' : entry.type === 'success' ? 'green' : undefined, mb: 1 }}>
                {entry.msg}
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Test; 